import {
  Button,
  Card,
  DataTable,
  EmptyState,
  Icon,
  Text,
  useToast,
  type DataTableColumn,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useSession } from '../../identity/session';
import { useDeleteMiteCount, useMiteCounts, type MiteCount } from '../api';
import { MiteCountDialog } from '../components/MiteCountDialog';
import { isFiltered, RecordToolbar, useRecordFilter } from '../components/RecordFilters';
import { PageActions } from '../../../app/PageActions';
import { useRowMenu } from '../../../app/rowActions';
import { formatInfestation } from '../format';
import styles from './RecordPages.module.css';

/** All mite counts (TRT-006). */
export function MiteCountsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useSession();
  const rowMenu = useRowMenu();
  const [filter, setFilter, resetFilter] = useRecordFilter();
  const columnSort = useColumnSort();
  const [recording, setRecording] = useState(false);
  const [editing, setEditing] = useState<MiteCount | null>(null);
  const [deleting, setDeleting] = useState<MiteCount | null>(null);
  // Paged list: the server sorts, so "Weitere laden" continues in order (#188).
  const list = useMiteCounts({
    ...filter,
    ...(columnSort.value ? { sort: columnSort.value } : {}),
  });
  const remove = useDeleteMiteCount();
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const write = can('treatments.write');

  const columns: DataTableColumn<MiteCount>[] = [
    {
      key: 'date',
      header: t('apiary.miteCounts.columns.date'),
      cell: (r) => formatDate(r.counted_on),
      sortValue: (r) => r.counted_on,
      width: '8rem',
    },
    {
      key: 'colony',
      header: t('apiary.miteCounts.columns.colony'),
      primary: true,
      cell: (r) => <Link to={`/voelker/${r.colony.id}`}>{r.colony.name}</Link>,
      sortValue: (r) => r.colony.name,
    },
    {
      key: 'location',
      header: t('apiary.miteCounts.columns.location'),
      cell: (r) => r.location?.name ?? '–',
      sortValue: (r) => r.location?.name,
    },
    {
      key: 'method',
      header: t('apiary.miteCounts.columns.method'),
      cell: (r) => t(`apiary.miteMethod.${r.method}`),
      sortValue: (r) => t(`apiary.miteMethod.${r.method}`),
    },
    {
      key: 'mites',
      header: t('apiary.miteCounts.columns.mites'),
      align: 'end',
      cell: (r) => r.mites,
      sortValue: (r) => r.mites,
    },
    {
      key: 'infestation',
      header: t('apiary.miteCounts.columns.infestation'),
      align: 'end',
      cell: (r) => formatInfestation(r.infestation),
      sortValue: (r) => r.infestation.value,
    },
  ];

  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <div className={styles.page}>
      {write && (
        <PageActions>
          <Button iconStart={<Icon name="plus" size="sm" />} onClick={() => setRecording(true)}>
            {t('apiary.miteCounts.record')}
          </Button>
        </PageActions>
      )}
      <RecordToolbar value={filter} onChange={setFilter} onReset={resetFilter} sort={sortMenu} />
      <Card padding="none" className={styles.card}>
        {list.error && (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-5)' }}>
            {errorMessage(list.error)}
          </Text>
        )}
        <DataTable
          caption={t('apiary.miteCounts.caption')}
          rows={rows}
          rowKey={(r) => r.id}
          {...columnSort}
          presorted
          loading={list.isPending}
          columns={columns}
          {...(write
            ? {
                rowActions: rowMenu.table<MiteCount>(
                  (r) => r.colony.name,
                  (r) => [rowMenu.edit(() => setEditing(r)), rowMenu.remove(() => setDeleting(r))],
                ),
              }
            : {})}
          empty={
            isFiltered(filter) ? (
              <EmptyState
                title={t('common.noResults')}
                actions={
                  <Button variant="secondary" onClick={resetFilter}>
                    {t('common.resetFilters')}
                  </Button>
                }
              />
            ) : (
              <EmptyState icon="bug" title={t('apiary.miteCounts.emptyTitle')} />
            )
          }
        />
        {list.hasNextPage && (
          <div className={styles.more}>
            <Button
              variant="secondary"
              loading={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              {t('apiary.loadMore')}
            </Button>
          </div>
        )}
      </Card>
      {recording && <MiteCountDialog open onClose={() => setRecording(false)} />}
      {editing && <MiteCountDialog open mite={editing} onClose={() => setEditing(null)} />}
      <ConfirmDialog
        open={deleting !== null}
        onClose={() => {
          remove.reset();
          setDeleting(null);
        }}
        title={t('apiary.miteCount.deleteTitle')}
        description={t('apiary.miteCount.deleteText')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : undefined}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.show(t('apiary.miteCount.deleted'));
              setDeleting(null);
            },
          })
        }
      />
    </div>
  );
}
